import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';

const ConfirmContext = createContext(null);
const PromptContext = createContext(null);

const CONFIRM_DEFAULTS = {
  title: '',
  description: '',
  confirmLabel: '',
  cancelLabel: '',
  destructive: false,
};

const PROMPT_DEFAULTS = {
  title: '',
  description: '',
  confirmLabel: '',
  cancelLabel: '',
  placeholder: '',
  defaultValue: '',
  inputType: 'text',
  required: false,
};

export function ConfirmProvider({ children }) {
  const { t } = useTranslation();

  // Confirm state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmOpts, setConfirmOpts] = useState(CONFIRM_DEFAULTS);
  const confirmResolver = useRef(null);

  // Prompt state
  const [promptOpen, setPromptOpen] = useState(false);
  const [promptOpts, setPromptOpts] = useState(PROMPT_DEFAULTS);
  const [promptValue, setPromptValue] = useState('');
  const promptResolver = useRef(null);
  const promptInputRef = useRef(null);

  const confirm = useCallback((options = {}) => {
    setConfirmOpts({ ...CONFIRM_DEFAULTS, ...options });
    setConfirmOpen(true);
    return new Promise((resolve) => {
      confirmResolver.current = resolve;
    });
  }, []);

  const prompt = useCallback((options = {}) => {
    const opts = { ...PROMPT_DEFAULTS, ...options };
    setPromptOpts(opts);
    setPromptValue(opts.defaultValue || '');
    setPromptOpen(true);
    return new Promise((resolve) => {
      promptResolver.current = resolve;
    });
  }, []);

  const resolveConfirm = useCallback((value) => {
    setConfirmOpen(false);
    if (confirmResolver.current) {
      confirmResolver.current(value);
      confirmResolver.current = null;
    }
  }, []);

  const resolvePrompt = useCallback((value) => {
    setPromptOpen(false);
    if (promptResolver.current) {
      promptResolver.current(value);
      promptResolver.current = null;
    }
  }, []);

  // Auto-focus prompt input when opened
  useEffect(() => {
    if (promptOpen) {
      const id = setTimeout(() => promptInputRef.current?.focus(), 50);
      return () => clearTimeout(id);
    }
  }, [promptOpen]);

  const confirmTitle = confirmOpts.title || t('confirm.defaultTitle', 'Are you sure?');
  const confirmCancelLabel = confirmOpts.cancelLabel || t('common.cancel', 'Cancel');
  const confirmConfirmLabel = confirmOpts.confirmLabel || t('common.confirm', 'Confirm');

  const promptTitle = promptOpts.title || t('confirm.promptTitle', 'Enter value');
  const promptCancelLabel = promptOpts.cancelLabel || t('common.cancel', 'Cancel');
  const promptConfirmLabel = promptOpts.confirmLabel || t('common.ok', 'OK');

  const submitDisabled = promptOpts.required && !promptValue.trim();

  const handlePromptSubmit = (e) => {
    e?.preventDefault?.();
    if (submitDisabled) return;
    resolvePrompt(promptValue);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      <PromptContext.Provider value={prompt}>
        {children}

        <AlertDialog open={confirmOpen} onOpenChange={(v) => { if (!v) resolveConfirm(false); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{confirmTitle}</AlertDialogTitle>
              {confirmOpts.description ? (
                <AlertDialogDescription>{confirmOpts.description}</AlertDialogDescription>
              ) : null}
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => resolveConfirm(false)}>{confirmCancelLabel}</AlertDialogCancel>
              <AlertDialogAction
                className={confirmOpts.destructive ? 'bg-destructive hover:bg-destructive/90' : undefined}
                onClick={() => resolveConfirm(true)}
              >
                {confirmConfirmLabel}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <Dialog open={promptOpen} onOpenChange={(v) => { if (!v) resolvePrompt(null); }}>
          <DialogContent>
            <form onSubmit={handlePromptSubmit}>
              <DialogHeader>
                <DialogTitle>{promptTitle}</DialogTitle>
                {promptOpts.description ? (
                  <DialogDescription>{promptOpts.description}</DialogDescription>
                ) : null}
              </DialogHeader>
              <div className="py-3">
                <Input
                  ref={promptInputRef}
                  type={promptOpts.inputType}
                  placeholder={promptOpts.placeholder}
                  value={promptValue}
                  onChange={(e) => setPromptValue(e.target.value)}
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => resolvePrompt(null)}>
                  {promptCancelLabel}
                </Button>
                <Button type="submit" disabled={submitDisabled}>
                  {promptConfirmLabel}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </PromptContext.Provider>
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    return async () => window.confirm('Confirm action?');
  }
  return ctx;
}

export function usePrompt() {
  const ctx = useContext(PromptContext);
  if (!ctx) {
    return async (opts = {}) => window.prompt(opts.title || 'Enter value:', opts.defaultValue || '');
  }
  return ctx;
}
